import { useEffect, useState, useCallback, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, FileText, Sparkles, Layers, CheckCircle2, XCircle, Download, X, ChevronRight, ChevronDown, Settings as SettingsIcon, AlertTriangle, FilePlus2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, openAuthedBlob } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import InlineInvoiceEditor from "@/components/InlineInvoiceEditor";

export default function Dashboard() {
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // {current, total, current_filename}
  const [bulkResult, setBulkResult] = useState(null);
  const [pendingBulk, setPendingBulk] = useState(null); // {supported:[File], skipped:[File], numbers:[""]}
  const [expandedRow, setExpandedRow] = useState(null); // invoice_id of expanded row
  const [invoices, setInvoices] = useState([]);
  const [sellerProfile, setSellerProfile] = useState(null);
  const pollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const loadInvoices = useCallback(async () => {
    try {
      const { data } = await api.get("/invoices");
      setInvoices(data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadInvoices();
    api.get("/settings/seller").then(({ data }) => setSellerProfile(data)).catch(() => {});
  }, [loadInvoices]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload a PDF file");
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/po/extract", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      sessionStorage.setItem("extracted_po", JSON.stringify(data));
      toast.success("Purchase Order extracted successfully");
      navigate("/extract");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to extract PO");
    } finally {
      setUploading(false);
    }
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const stageBulk = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const isSupported = (f) => {
      const n = f.name.toLowerCase();
      return n.endsWith(".pdf") || n.endsWith(".xlsx") || n.endsWith(".xls");
    };
    const supported = files.filter(isSupported);
    const skipped = files.filter((f) => !isSupported(f));
    if (supported.length === 0) {
      toast.error("No supported files. Only PDF / Excel are accepted.");
      return;
    }
    // Precheck each file — detects bulk template Excels so the staging table
    // can show the row-group summary and skip asking for invoice numbers that
    // are already written in the sheet.
    let fileInfos = supported.map((f) => ({
      filename: f.name,
      kind: "po",
      invoices: null,
    }));
    try {
      const form = new FormData();
      supported.forEach((f) => form.append("files", f));
      const { data } = await api.post("/po/bulk/precheck", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      if (Array.isArray(data?.files)) fileInfos = data.files;
    } catch (e) {
      console.warn("Bulk precheck failed; continuing without template detection", e);
    }
    setPendingBulk({
      supported,
      skipped,
      numbers: supported.map(() => ""),
      infos: fileInfos,
    });
  };

  const clearStaging = () => setPendingBulk(null);

  const runBulk = async () => {
    if (!pendingBulk) return;
    const { supported, skipped, numbers } = pendingBulk;
    const skippedRows = skipped.map((f) => ({
      filename: f.name,
      status: "failed",
      invoice_number: null,
      invoice_id: null,
      po_number: null,
      grand_total: null,
      error: "Unsupported file type — only PDF or Excel (.xlsx/.xls) allowed",
    }));

    setBulkBusy(true);
    setBulkResult(null);
    setBulkProgress({ current: 0, total: supported.length, current_filename: null });

    const form = new FormData();
    supported.forEach((f) => form.append("files", f));
    numbers.forEach((n) => form.append("invoice_numbers", (n || "").trim()));

    let jobId;
    try {
      const { data } = await api.post("/po/bulk", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      jobId = data.job_id;
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || e?.message || "Failed to start bulk job");
      setBulkBusy(false);
      setBulkProgress(null);
      return;
    }

    setPendingBulk(null); // clear staging once job has started

    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const { data: st } = await api.get(`/po/bulk/${jobId}/status`);
        setBulkProgress({
          current: st.current || 0,
          total: st.total || supported.length,
          current_filename: st.current_filename,
        });
        if (st.status === "done" || st.status === "error") {
          setBulkResult({
            ...st,
            total: st.total + skippedRows.length,
            failed_count: (st.failed_count || 0) + skippedRows.length,
            results: [...skippedRows, ...(st.results || [])],
          });
          stopPolling();
          setBulkBusy(false);
          setBulkProgress(null);
          loadInvoices();
          const s = st.success_count || 0;
          const f = (st.failed_count || 0) + skippedRows.length;
          if (st.status === "error") {
            toast.error(`Bulk job failed: ${st.error || "unknown error"}`);
          } else if (f === 0) {
            toast.success(`All ${s} invoice${s > 1 ? "s" : ""} ready — review below, then Download All as ZIP`);
          } else if (s === 0) {
            toast.error(`All ${f} file${f > 1 ? "s" : ""} failed — see details below`);
          } else {
            toast(`${s} succeeded, ${f} failed — review below, then Download All as ZIP`);
          }
        }
      } catch (err) {
        console.error(err);
      }
    }, 1500);
  };

  const totalRevenue = invoices.reduce((s, i) => s + (i.grand_total || 0), 0);

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
            Dashboard
          </div>
          <h1 className="text-4xl font-bold tracking-tight" data-testid="dashboard-heading">
            Generate GST Tax Invoices from POs
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            Upload any Purchase Order PDF — our AI reads it and produces a
            compliant Indian GST invoice using your saved Seller Profile.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => navigate("/manual")}
            data-testid="dashboard-manual-btn"
          >
            <FilePlus2 size={14} className="mr-1" /> Create Manual Invoice
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate("/settings")} data-testid="dashboard-settings-btn">
            <SettingsIcon size={14} className="mr-1" /> Seller Settings
          </Button>
          <Badge variant="outline" className="mono text-xs" data-testid="ai-badge">
            <Sparkles size={12} className="mr-1" />
            Gemini 3 Flash
          </Badge>
        </div>
      </div>

      {sellerProfile && (!sellerProfile.name || !sellerProfile.gstin) && (
        <Card className="mb-6 p-4 border-amber-300 bg-amber-50/60" data-testid="setup-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-amber-900">Set up your Seller Profile first</div>
              <div className="text-amber-800 text-[12px] mt-0.5">
                Add your Company Name, GSTIN and address so every generated invoice
                carries your details automatically.
              </div>
            </div>
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => navigate("/settings")}
              data-testid="setup-banner-cta"
            >
              Open Seller Settings →
            </Button>
          </div>
        </Card>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-3 gap-6 mb-8">
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
            Total Invoices
          </div>
          <div className="text-3xl font-semibold mono tabular" data-testid="kpi-total-invoices">
            {invoices.length}
          </div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
            Total Invoiced (₹)
          </div>
          <div className="text-3xl font-semibold mono tabular" data-testid="kpi-total-revenue">
            {totalRevenue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
          </div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
            Last Invoice
          </div>
          <div className="text-lg font-medium mono truncate" data-testid="kpi-last-invoice">
            {invoices[0]?.invoice_number || "—"}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            {invoices[0]?.invoice_date || "No invoices yet"}
          </div>
        </Card>
      </div>

      {/* Upload zones */}
      <div className="grid grid-cols-3 gap-6 mb-8">
        {/* Single upload */}
        <Card className="col-span-2 p-2">
          <label
            className={`dropzone ${drag ? "active" : ""} rounded-md flex flex-col items-center justify-center py-16 cursor-pointer`}
            onDragOver={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              const f = e.dataTransfer.files?.[0];
              handleFile(f);
            }}
            data-testid="po-dropzone"
          >
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="po-file-input"
            />
            <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-4">
              <UploadCloud size={28} strokeWidth={1.5} className="text-blue-600" />
            </div>
            <div className="text-base font-medium">
              {uploading ? "Extracting Purchase Order…" : "Drag & Drop a Purchase Order PDF"}
            </div>
            <div className="text-sm text-zinc-500 mt-1">
              Single PO — review before generating invoice
            </div>
            {uploading && (
              <div className="mt-4 text-xs text-blue-600 mono uppercase tracking-wider">
                Reading PO with AI…
              </div>
            )}
          </label>
        </Card>

        {/* Bulk upload */}
        <Card className="p-2">
          <label
            className="dropzone rounded-md flex flex-col items-center justify-center py-16 cursor-pointer text-center px-4"
            data-testid="bulk-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (!bulkBusy) stageBulk(e.dataTransfer.files);
            }}
          >
            <input
              type="file"
              accept="application/pdf,.pdf,.xlsx,.xls"
              multiple
              className="hidden"
              disabled={bulkBusy}
              onChange={(e) => stageBulk(e.target.files)}
              data-testid="bulk-file-input"
            />
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
              <Layers size={24} strokeWidth={1.5} className="text-emerald-600" />
            </div>
            <div className="text-sm font-medium">
              {bulkBusy
                ? `Processing ${bulkProgress?.current || 0} of ${bulkProgress?.total || 0}…`
                : "Bulk Upload"}
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              Select multiple PDF / Excel POs — you can name each invoice
            </div>
            {bulkBusy && (
              <div className="mt-4 text-[10px] text-emerald-700 mono uppercase tracking-wider">
                Sequential processing — one at a time
              </div>
            )}
          </label>
          <div className="px-4 py-2 border-t border-zinc-100 flex items-center justify-between gap-2">
            <div className="text-[11px] text-zinc-500">
              Not sure of the format?
            </div>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              data-testid="bulk-download-template-btn"
            >
              <a
                href={`${API}/po/sample-template`}
                download="bulkinvoice-sample-po.xlsx"
              >
                <Download size={13} className="mr-1" />
                Download Sample Template
              </a>
            </Button>
          </div>
        </Card>
      </div>

      {/* Staging: per-file invoice number editor */}
      {pendingBulk && !bulkBusy && (
        <Card className="mb-8 overflow-hidden" data-testid="bulk-staging-panel">
          <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold">Uploaded POs — Assign Invoice Numbers</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">
                Leave blank to auto-name as <span className="mono">INV-&lt;PO Number&gt;</span>.
                Only starts processing when you click <b>Generate</b>.
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={clearStaging} data-testid="bulk-cancel-staging">
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={runBulk}
                data-testid="bulk-generate-btn"
              >
                {(() => {
                  const n = (pendingBulk.infos || []).reduce((acc, info, i) => {
                    if (info?.kind === "template" && Array.isArray(info.invoices)) {
                      return acc + info.invoices.length;
                    }
                    return acc + 1;
                  }, 0) || pendingBulk.supported.length;
                  return `Generate ${n} Invoice${n > 1 ? "s" : ""}`;
                })()}
              </Button>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-zinc-500 bg-white">
              <tr className="text-left">
                <th className="py-2 px-4 font-medium w-8">#</th>
                <th className="py-2 px-4 font-medium">PO File</th>
                <th className="py-2 px-4 font-medium w-72">Invoice Number (optional)</th>
                <th className="py-2 px-4 font-medium w-12 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {pendingBulk.supported.map((f, i) => {
                const info = (pendingBulk.infos || [])[i];
                const isTemplate = info?.kind === "template";
                return (
                <tr key={i} className="border-t border-zinc-100" data-testid={`bulk-staging-row-${i}`}>
                  <td className="py-2 px-4 text-zinc-500 mono">{i + 1}</td>
                  <td className="py-2 px-4 truncate max-w-[380px]" title={f.name}>
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-zinc-400 shrink-0" />
                      <span>{f.name}</span>
                      <span className="text-[10px] text-zinc-400 mono">
                        {(f.size / 1024).toFixed(1)} KB
                      </span>
                      {isTemplate && (
                        <span
                          className="text-[10px] mono uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"
                          data-testid={`bulk-template-badge-${i}`}
                        >
                          Template × {info.invoices?.length || 0}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-4">
                    {isTemplate ? (
                      <div
                        className="text-[11px] text-zinc-500 leading-snug"
                        data-testid={`bulk-staging-template-${i}`}
                      >
                        {(info.invoices || []).slice(0, 3).map((iv, j) => (
                          <div key={j} className="mono truncate">
                            {iv.invoice_number || "(auto)"} · {iv.buyer_name || "—"} · {iv.items} item
                            {iv.items === 1 ? "" : "s"}
                          </div>
                        ))}
                        {(info.invoices || []).length > 3 && (
                          <div className="text-[10px] text-zinc-400">
                            + {info.invoices.length - 3} more
                          </div>
                        )}
                      </div>
                    ) : (
                      <Input
                        value={pendingBulk.numbers[i]}
                        onChange={(e) => {
                          const nums = [...pendingBulk.numbers];
                          nums[i] = e.target.value;
                          setPendingBulk({ ...pendingBulk, numbers: nums });
                        }}
                        placeholder="Auto: INV-<PO Number>"
                        className="h-8 text-xs mono"
                        data-testid={`bulk-staging-invno-${i}`}
                      />
                    )}
                  </td>
                  <td className="py-2 px-4 text-right">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-zinc-400 hover:text-red-600"
                      onClick={() => {
                        const supported = pendingBulk.supported.filter((_, j) => j !== i);
                        const numbers = pendingBulk.numbers.filter((_, j) => j !== i);
                        const infos = (pendingBulk.infos || []).filter((_, j) => j !== i);
                        if (supported.length === 0) {
                          setPendingBulk(null);
                        } else {
                          setPendingBulk({ ...pendingBulk, supported, numbers, infos });
                        }
                      }}
                      data-testid={`bulk-staging-remove-${i}`}
                    >
                      <X size={14} />
                    </Button>
                  </td>
                </tr>
                );
              })}
              {pendingBulk.skipped.map((f, i) => (
                <tr key={`s-${i}`} className="border-t border-zinc-100 bg-red-50/40" data-testid={`bulk-staging-skipped-${i}`}>
                  <td className="py-2 px-4 text-red-500 mono">—</td>
                  <td className="py-2 px-4 truncate max-w-[380px]" title={f.name}>
                    <div className="flex items-center gap-2 text-red-700">
                      <XCircle size={14} className="shrink-0" />
                      <span>{f.name}</span>
                    </div>
                  </td>
                  <td colSpan="2" className="py-2 px-4 text-xs text-red-700">
                    Skipped — only PDF or Excel (.xlsx/.xls) allowed
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Progress bar shown while background job is running */}
      {bulkBusy && bulkProgress && (
        <Card className="mb-8 p-5" data-testid="bulk-progress-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold">
              Processing invoice{" "}
              <span className="mono" data-testid="bulk-progress-current">
                {bulkProgress.current}
              </span>{" "}
              of{" "}
              <span className="mono" data-testid="bulk-progress-total">
                {bulkProgress.total}
              </span>
            </div>
            <div className="text-xs text-zinc-500 mono truncate max-w-md text-right" data-testid="bulk-progress-filename">
              {bulkProgress.current_filename || "…"}
            </div>
          </div>
          <Progress
            value={bulkProgress.total ? (bulkProgress.current / bulkProgress.total) * 100 : 0}
            className="h-2"
            data-testid="bulk-progress-bar"
          />
          <div className="text-[10px] text-zinc-500 mt-2 uppercase tracking-wider">
            POs are processed one at a time. This page can stay open — you may keep working.
          </div>
        </Card>
      )}

      {/* Bulk results panel */}
      {bulkResult && (
        <Card className="mb-8 overflow-hidden" data-testid="bulk-result-panel">
          <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-sm font-semibold">Bulk Processing Report</div>
              <Badge variant="outline" className="text-xs" data-testid="bulk-success-badge">
                <CheckCircle2 size={12} className="mr-1 text-emerald-600" />
                {bulkResult.success_count} success
              </Badge>
              {bulkResult.failed_count > 0 && (
                <Badge variant="outline" className="text-xs" data-testid="bulk-failed-badge">
                  <XCircle size={12} className="mr-1 text-red-600" />
                  {bulkResult.failed_count} failed
                </Badge>
              )}
            </div>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setBulkResult(null); setExpandedRow(null); }} data-testid="bulk-close">
              <X size={16} />
            </Button>
          </div>
          <table className="w-full text-sm">
            <thead className="text-zinc-500 bg-white">
              <tr className="text-left">
                <th className="py-2 px-4 font-medium w-8"></th>
                <th className="py-2 px-4 font-medium w-8">#</th>
                <th className="py-2 px-4 font-medium">File</th>
                <th className="py-2 px-4 font-medium w-24">Status</th>
                <th className="py-2 px-4 font-medium">Invoice / Reason</th>
                <th className="py-2 px-4 font-medium text-right w-32">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {bulkResult.results.map((r, i) => {
                const isSuccess = r.status === "success";
                const isExpanded = expandedRow === r.invoice_id;
                return (
                  <Fragment key={i}>
                    <tr
                      className={`border-t border-zinc-100 ${isSuccess ? "cursor-pointer hover:bg-zinc-50" : ""}`}
                      onClick={() => isSuccess && r.invoice_id && setExpandedRow(isExpanded ? null : r.invoice_id)}
                      data-testid={`bulk-row-${i}`}
                    >
                      <td className="py-2 px-2 text-zinc-400">
                        {isSuccess && (isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
                      </td>
                      <td className="py-2 px-4 text-zinc-500 mono">{i + 1}</td>
                      <td className="py-2 px-4 truncate max-w-[260px]" title={r.filename}>{r.filename}</td>
                      <td className="py-2 px-4">
                        {isSuccess ? (
                          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid={`bulk-status-success-${i}`}>
                            Success
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800 hover:bg-red-100" data-testid={`bulk-status-failed-${i}`}>
                            Failed
                          </Badge>
                        )}
                      </td>
                      <td className="py-2 px-4 text-xs">
                        {isSuccess ? (
                          <span className="mono text-blue-700" data-testid={`bulk-invoice-number-${i}`}>
                            {r.invoice_number}
                          </span>
                        ) : (
                          <span className="text-red-700" data-testid={`bulk-error-${i}`}>{r.error || "Unknown error"}</span>
                        )}
                      </td>
                      <td className="py-2 px-4 mono tabular text-right">
                        {isSuccess && r.grand_total != null
                          ? Number(r.grand_total).toLocaleString("en-IN", { minimumFractionDigits: 2 })
                          : "—"}
                      </td>
                    </tr>
                    {isSuccess && isExpanded && r.invoice_id && (
                      <tr className="bg-zinc-50">
                        <td colSpan="6" className="p-0">
                          <InlineInvoiceEditor
                            invoiceId={r.invoice_id}
                            onUpdated={(updated) => {
                              setBulkResult((br) => {
                                if (!br) return br;
                                return {
                                  ...br,
                                  results: br.results.map((x) =>
                                    x.invoice_id === updated.id
                                      ? { ...x, grand_total: updated.grand_total, invoice_number: updated.invoice_number }
                                      : x,
                                  ),
                                };
                              });
                            }}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          {bulkResult.success_count > 0 && bulkResult.zip_url && (
            <div className="px-5 py-4 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between">
              <div className="text-xs text-zinc-500">
                Review any invoice above, edit if needed, then download all as a single ZIP.
              </div>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                size="lg"
                onClick={async () => {
                  try {
                    const path = bulkResult.zip_url.replace(/^\/api/, "");
                    await openAuthedBlob(path, {
                      filename: bulkResult.zip_filename || "invoices.zip",
                    });
                  } catch {
                    toast.error("Failed to download ZIP");
                  }
                }}
                data-testid="bulk-download-all-zip"
              >
                <Download size={16} className="mr-2" /> Download All as ZIP ({bulkResult.success_count})
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Recent invoices */}
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-xl font-semibold">Recent Invoices</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/history")}
          data-testid="view-all-invoices"
        >
          View all →
        </Button>
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500">
            <tr className="text-left">
              <th className="py-3 px-4 font-medium">Invoice #</th>
              <th className="py-3 px-4 font-medium">Date</th>
              <th className="py-3 px-4 font-medium">Buyer</th>
              <th className="py-3 px-4 font-medium">PO Ref</th>
              <th className="py-3 px-4 font-medium text-right">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr>
                <td colSpan="5" className="py-12 text-center text-zinc-400">
                  <FileText size={22} className="inline mb-2" />
                  <div className="text-sm">No invoices yet. Upload a PO to begin.</div>
                </td>
              </tr>
            ) : (
              invoices.slice(0, 5).map((inv) => (
                <tr
                  key={inv.id}
                  className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                  onClick={() => navigate(`/invoice/${inv.id}`)}
                  data-testid={`invoice-row-${inv.invoice_number}`}
                >
                  <td className="py-3 px-4 mono font-medium">{inv.invoice_number}</td>
                  <td className="py-3 px-4 text-zinc-600">{inv.invoice_date}</td>
                  <td className="py-3 px-4 truncate max-w-[240px]">
                    {inv.buyer?.name}
                  </td>
                  <td className="py-3 px-4 mono text-zinc-500 text-xs">
                    {inv.po_number}
                  </td>
                  <td className="py-3 px-4 mono tabular text-right">
                    {inv.grand_total?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
